import { SubjectArea } from '../../interfaces/EOAInterface';
import { CourseMappingGroup } from '../../interfaces/CourseMappingInterface';
import { formatLanguageLevel } from './courseMappingCellFormatters';

export interface CourseMappingDropdownOption {
  value: string;
  label: string;
}

export const LANGUAGE_LEVEL_OPTIONS: CourseMappingDropdownOption[] = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4+' },
];

export const parseLanguageLevelValue = (value: string): number => Number(value);

// The "4+" option is a bucket covering every level >= 4. When a course is already 4+, keep its
// stored value instead of collapsing it to 4, so re-selecting "4+" never overwrites a 5 or 6 with 4.
export const resolveLanguageLevelSelection = (
  selectedOptionValue: string,
  currentLevel: number | null | undefined
): number => {
  const parsed = parseLanguageLevelValue(selectedOptionValue);

  if (parsed >= 4 && currentLevel != null && currentLevel >= 4) {
    return currentLevel;
  }

  return parsed;
};

export const toLanguageLevelOptionValue = (level: number | null | undefined): string | null => {
  if (level == null) {
    return null;
  }

  return level >= 4 ? '4' : String(level);
};

export const toLanguageLevelDisplayLabel = (level: number | null | undefined): string | null =>
  formatLanguageLevel(level);

export const toSubjectAreaOptions = (subjectAreas: SubjectArea[]): CourseMappingDropdownOption[] =>
  subjectAreas.map(({ name }) => ({
    value: name,
    label: name,
  }));

export const toSubjectSubcategoryOptions = (
  subjectAreas: SubjectArea[],
  subjectArea: string | null
): CourseMappingDropdownOption[] => {
  const subcategories = subjectAreas.find((entry) => entry.name === subjectArea)?.subcategories ?? [];

  return subcategories
    .filter((entry) => entry.name.trim())
    .map((entry) => ({
      value: entry.name,
      label: entry.name,
    }));
};

export const hasSubjectSubcategoryOptions = (subjectAreas: SubjectArea[], subjectArea: string | null): boolean =>
  toSubjectSubcategoryOptions(subjectAreas, subjectArea).length > 0;

export const isLanguageDropdownAvailable = (subjectArea: string | null): boolean => subjectArea === 'World Language';

const collectUniqueValues = (
  groups: CourseMappingGroup[],
  getValue: (group: CourseMappingGroup) => string | null | undefined
): CourseMappingDropdownOption[] => {
  const values = new Set<string>();

  groups.forEach((group) => {
    const value = getValue(group);
    if (value?.trim()) {
      values.add(value.trim());
    }
  });

  return Array.from(values)
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    .map((value) => ({ value, label: value }));
};

export const collectLanguageCategoryOptions = (groups: CourseMappingGroup[]): CourseMappingDropdownOption[] =>
  collectUniqueValues(groups, (group) => group.attributes.language_category);

export const mergeDropdownOptions = (
  baseOptions: CourseMappingDropdownOption[],
  selectedValue: string | null
): CourseMappingDropdownOption[] => {
  if (!selectedValue || baseOptions.some((option) => option.value === selectedValue)) {
    return baseOptions;
  }

  return [{ value: selectedValue, label: selectedValue }, ...baseOptions];
};
